import { useMemo } from 'react';

import { QueryExports } from '@/__generated__/graphql';
import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useDiscoveryDefinitionSavedQueries } from '@/hooks/discovery/definitionSavedQueries';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  name: string;
  uniqueId: string;
  description: string | null;
};

type SavedQueryDefinitionListNode = {
  uniqueId: string;
  description: string | null;
  queryParams: any | null;
  exports: QueryExports[];
};

const getTableData = (
  saved_query: SavedQueryDefinitionListNode | undefined | null,
): TableData | undefined => {
  if (!saved_query) return undefined;
  const uniqueId = parseUniqueId(saved_query.uniqueId);
  return {
    name: uniqueId.remainder,
    uniqueId: uniqueId.raw,
    description: saved_query.description,
  };
};

export const SavedQueryFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const paginatedSavedQueryQuery = useDiscoveryDefinitionSavedQueries({
    environmentId,
    userId,
    first: pageSize,
  });
  const savedQueries = paginatedSavedQueryQuery.data;
  const pageInfo = paginatedSavedQueryQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return savedQueries?.map(getTableData);
  }, [savedQueries]);

  if (paginatedSavedQueryQuery.error)
    return <>{paginatedSavedQueryQuery.error.stack}</>;
  const isLoadingMore = paginatedSavedQueryQuery.fetching;
  const isInitialLoad =
    paginatedSavedQueryQuery.fetching && paginatedSavedQueryQuery.data === undefined;

  return (
    <>
      <PageHeading className="mb-10">Saved Queries</PageHeading>
      <ResourceFilterTable
        pluralizedResultName="saved queries"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            size: 60,
            minSize: 240,
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: 'Description',
            accessorKey: 'description',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedSavedQueryQuery.fetchMore}
      />
    </>
  );
};
